// Domain-local constants: column min-widths, drawer sizing, static labels.
import type { StatusPillMeta } from "../../constants";

export const DOMAIN_DRAWER_WIDTH_CLS = "w-[1400px] max-w-[95vw]";
export const DOMAIN_TABLE_MAX_HEIGHT = "500px";
export const DOMAIN_RULE_VALIDATION_SCOPE_KEY = "SCRG";
export const DOMAIN_RULE_VALIDATION_LAYER = "domain";
export const DOMAIN_RULE_VALIDATION_OUTCOME = "satisfied";


export const DOMAIN_DRAWER_TABS = {
  overview: "Overview",
  validation: "Rule Validation",
} as const;
export const DOMAIN_VALIDATION_COPY = {
  summary: "Rule Validation Summary",
  summaryDescription: "Validation of domain-specific rules for this document",
  allRules: "All Rules",
  passed: "Passed",
  notFound: "Not Found",
  totalRules: "Total",
  noRules: "No domain validation rules returned for this document.",
  loading: "Loading rule validation...",
  error: "Unable to load rule validation.",
  /* Evidence-coverage chart: rules grouped by how much evidence backs them */
  evidenceCoverage: "Evidence Coverage",
  evidenceCoverageSub: "Rules grouped by supporting evidence identified",
  bucketZero: "No evidence identified",
  bucketOne: "1 evidence item",
  bucketMany: "2+ evidence items",
  /* Caption under each bucket: "40% of rules" (share = count / rules). */
  bucketShare: "% of rules",
  bucketShareEmpty: "—",
  evidenceNone: "No evidence",
  evidenceItemSingular: "evidence item",
  evidenceItemPlural: "evidence items",
  rulesMeasure: "Rules",
  all: "All",
  searchPlaceholder: "Search",
  searchAria: "Search rules",
  sortBy: "Sort by",
  sortOriginal: "Original order",
  sortStatus: "Status",
  sortName: "Rule name",
  sortEvidence: "Evidence",
  noMatches: "No rules match your search.",
  clearFilters: "Clear search and filters",
  ruleConfidence: "Rule confidence",
  /* Single-page rule detail (semantic-rules catalog) */
  detailsLoading: "Loading rule details...",
  detailsError: "Unable to load rule details.",
  detailsMissing: "No catalog details found for this rule.",
  retry: "Retry",
  ruleKeyLabel: "Rule",
  domainLabel: "Domain",
  examplePassLabel: "Considered as pass",
  exampleFailLabel: "Considered as fail",
  /* Evidence excerpts in the rule detail (domain findings endpoint) */
  evidenceLabel: "Evidence",
  evidenceSub: "Matched in this document",
  findingSection: "Section",
  findingPage: "Page",
  evidenceNoText: "No excerpt returned for this evidence.",
} as const;

/* ── Rule Validation chart colours ──
 * Raw hex on purpose: the chart components take colour values, not Tailwind
 * classes. Keys mirror the slices/series they paint in ruleValidationPanel. */
export const RULE_VALIDATION_COLORS = {
  /** "Passed" donut slice (emerald-500). */
  passed: "#10b981",
  /** "Not Found" donut slice (red-500). */
  notFound: "#ef4444",
  evidenceBuckets: "#6366f1",

  noEvidence: "#ef4444",
} as const;

export const DOMAIN_COVERAGE_COL_MIN_WIDTH = {
  code: 80,
  name: 180,
  requirements: 110,
  share: 200,
  refs: 120,
  strength: 120,
  confidence: 100,
} as const;

export const DOMAIN_COVERAGE_COL_HEADERS = {
  code: "Domain",
  name: "Name",
  requirements: "Requirements",
  share: "Document Share",
  refs: "SCF References",
  strength: "Match Strength",
  confidence: "Confidence",
} as const;

export const DOMAIN_STATUS_PILL: Record<"stopped" | "validated", StatusPillMeta> = {
  stopped: {
    label: "Stopped early",
    cls: "bg-amber-500/15 text-amber-400",
    dot: "bg-amber-400",
  },
  validated: {
    label: "Validated",
    cls: "bg-emerald-500/15 text-emerald-400",
    dot: "bg-emerald-400",
  },
};

export const DOMAIN_BANNER = {
  stopped: {
    title: "Classification stopped early",
    body:
      "The domain classification was interrupted before it could finish. Results below may be incomplete.",
  },
  zeroRequirements: {
    title: "No normative requirements were extracted",
    body:
      "Domain matches shown are based on semantic similarity to the taxonomy definitions, not on policy statements. This document may not be a policy, or may not use the \"shall / must / should\" language the extractor looks for.",
  },
  taxonomyWarnings: {
    title: "Taxonomy warnings",
  },
} as const;

export const DOMAIN_HEADER_COPY = {
  title: "Domain Intelligence",
  description:
    "SCF domain classification and contextual relevance identified from this document.",
  overallLabel: "Overall domain confidence",
  frameworkFallback: "SCF",
  frameworkPrefix: "Framework · ",
};

export const DOMAIN_SUMMARY_LABELS = {
  requirementsAnalysed: "Normative clauses analysed",
  requirementsMapped: "Matched to a domain",
  requirementsUnmapped: "Unmapped clauses",
  domainsCovered: "SCF domains covered",
  mappings: "Total domain matches",
  multiDomain: "Matched to 2+ domains",
  overallConfidence: "Classification confidence",
  primary: "Primary domain match",
  primaryRight: "Strongest alignment",
  summary: "Domain classification summary",
  summaryRight: "SCF semantic mapping",
  all: "All domain mappings",
  allRightSuffix: "domains",
  emptyCoverage: "No domain coverage returned for this document.",
  mappedHeadlineCaption: "of normative clauses matched to a domain",
  flowClauses: "Normative clauses",
  flowClausesHint: "Extracted from the document",
  flowMatched: "Matched to domains",
  flowMatchedHint: "Reached at least one SCF domain",
  flowMappings: "Domain matches",
  flowMappingsHint: "A clause can match more than one domain",
  breadthLabel: "SCF domains represented",
  breadthCaption: "Distinct domains touched by the document",
  multiDomainCaption: "Broadly-scoped clauses",
  confidenceCaption: "Strongest domain alignment",
} as const;

export const DOMAIN_PRIMARY_LABELS = {
  primaryRefs: "Primary references",
  primaryRefsHint: "Clauses where this domain is the top match",
  secondaryRefs: "Secondary references",
  secondaryRefsHint: "Clauses that also point here",
  shareOfDocument: "Share of document",
  shareCaption: "Of all normative clauses",
  matchStrength: "Match strength",
  domainMatch: "Domain match",
  classificationConfidence: "Classification confidence",
  semanticAlignment: "Semantic alignment",
  scfPurpose: "SCF purpose",
  clausesPrimary: "Primary clauses",
  clausesSecondary: "Secondary clauses",
  moreSuffix: "more",
  aiTag: "AI",
  aiSection: "Semantic explanation",
  ringCaption: "Match",
  noPrimary:
    "No requirements mapped to any SCF domain as a primary reference. The document may not be a policy. See the full mapping table below for any secondary (semantic) matches.",
} as const;

export const CLAUSE_PREVIEW_MAX = 4;

export type DomainCoverageStatus =
  | "FULLY_COVERED"
  | "PARTIALLY_COVERED"
  | "WEAKLY_COVERED"
  | "NOT_COVERED";

export interface CoverageStatusMeta {
  label: string;
  cls: string;
  dot: string;
}

export const DOMAIN_COVERAGE_STATUS_META: Record<DomainCoverageStatus, CoverageStatusMeta> = {
  FULLY_COVERED: {
    label: "Fully covered",
    cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    dot: "bg-emerald-400",
  },
  PARTIALLY_COVERED: {
    label: "Partially covered",
    cls: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    dot: "bg-amber-400",
  },
  WEAKLY_COVERED: {
    label: "Weakly covered",
    cls: "border-orange-500/30 bg-orange-500/10 text-orange-400",
    dot: "bg-orange-400",
  },
  NOT_COVERED: {
    label: "Not covered",
    cls: "border-slate-500/30 bg-slate-500/10 text-slate-400",
    dot: "bg-slate-400",
  },
};


